"use client";

import { useAuth } from "@/hooks/use-auth";
import { useBuyerActions } from "@/hooks/use-buyer-actions";
import { useGuardedAction } from "@/hooks/use-guarded-action";
import { useLoginDialog } from "@/hooks/use-login-dialog";
import { useComparisonStoreData } from "@/providers/global";
import { useBuyerPropertiesStore } from "@/providers/global/stores/buyer-properties-store";
import { BuyerProject } from "@/types/buyerProject";
import { SellerUnitsAndProjectsOne } from "@/types/sup";
import { Heart } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "../rac/button";
import { CompareButton } from "../search/compare/compare-button";

interface ProjectHeaderProps {
	name: string;
	location: string;
	projectId: string;
	className?: string;
	supOne?: SellerUnitsAndProjectsOne;
}

export function ProjectTitle({ name, location, projectId, className = "", supOne }: ProjectHeaderProps) {
	const { isAuthenticated } = useAuth();
	const { openDialog } = useLoginDialog();
	const { isProjectShortlisted } = useBuyerPropertiesStore();
	const { handleShortlist, handleRemoveShortlist, isShortlistLoading } = useBuyerActions();
	const [logged, setLogged] = useState(false);

	// --- Derived State ---
	const isShortlisted = isProjectShortlisted(projectId);

	// --- Comparison tray integration ---
	const comparisonTray = useComparisonStoreData(state => state.comparisonTray);
	const toggleComparison = useComparisonStoreData(state => state.toggleComparison);
	const isInComparison = supOne ? comparisonTray?.some(f => f._id === supOne._id) || false : false;

	// --- Shortlist Handler Logic (without guard) ---
	const performShortlistAction = useCallback(async () => {
		if (!isAuthenticated) {
			toast.error("Please login to shortlist this project", {
				duration: 3000,
				position: "bottom-right",
			});
			return;
		}

		const property = {
			__typename: "BuyerProject" as const,
			id: projectId,
			project: { id: projectId },
		} as BuyerProject;

		if (isShortlisted) {
			await handleRemoveShortlist({ property, source: "project_title" });
		} else {
			await handleShortlist(property, "project_title");
		}
	}, [projectId, isShortlisted, isAuthenticated, handleShortlist, handleRemoveShortlist]);

	// Use guarded action hook
	const { guardedAction: handleShortlistAction, GuardComponent } = useGuardedAction(
		"shortlist_property",
		performShortlistAction,
	);

	const reqButtonTitle = isShortlistLoading
		? "Processing"
		: isShortlisted
			? "Project shortlisted"
			: "Shortlist project";
	useEffect(() => {
		if (logged) {
			handleShortlistAction();
			setLogged(false); // Reset after action to prevent infinite loop
		}
	}, [logged, handleShortlistAction]);

	return (
		<div className={`flex justify-between items-start gap-3 px-4 ${className}`}>
			<div className="">
				<h1 className="text-2xl truncate font-medium text-gray-900 mb-1">{name}</h1>
				<div className="text-xs font-semibold text-gray-500">{location}</div>
			</div>

			<div className="flex items-center gap-4" title={reqButtonTitle}>
				{/* Compare Button - only show if supOne is provided */}
				{supOne && (
					<CompareButton
						property={supOne}
						isInComparison={isInComparison}
						onToggleComparison={toggleComparison}
						variant="icon"
					/>
				)}
				{!isAuthenticated ? (
					<Button
						variant="bareshell"
						isPending={isShortlistLoading}
						aria-label="Log in to your account to shortlist project"
						className="p-2 group/heart cursor-pointer"
						onClick={() => openDialog({ onLoginSuccess: () => setLogged(true) })}
					>
						<Heart className="w-6 h-6 transition-colors fill-gray-300 text-gray-400 hover:fill-red-200 hover:text-red-400" />
					</Button>
				) : (
					<Button
						isPending={isShortlistLoading}
						className="p-2 group/heart cursor-pointer"
						aria-label={isShortlisted ? "Remove from favorites" : "Add to favorites"}
						onClick={handleShortlistAction}
						variant="bareshell"
					>
						<Heart
							className={`w-6 h-6 transition-colors ${
								isShortlisted
									? "fill-red-500 text-red-500"
									: "fill-gray-300 text-gray-400 hover:fill-red-200 hover:text-red-400"
							} ${isShortlistLoading ? "opacity-50" : ""}`}
						/>
					</Button>
				)}
			</div>

			{/* Relationship Guard Modal */}
			<GuardComponent />
		</div>
	);
}
